import React, { useContext, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import RecorderBox from "./Recorder";
import FinalFormModal from "./Modal";
import CriteriaSorter from "./CriteriaSorter";
import MultiStepRatingForm from "./MultiStepRatingForm";
import OverallScoreSection from "./OverallScoreSection";
import "./style-rtl.scss";
import { useTranslation } from "react-i18next";
import { GlobalStateContext } from "./../../layouts/GlobalStates";
import { scrollIntoView } from "../helperFunctions";
import DBButton from "../../component/global/DBButton";
import { RewardBadge } from "../../features/incentives/Rewards";

const FEEDBACK_FORM_DISPLAY_STEP = "step";

export default function Rating() {
    const { t } = useTranslation();

    const { feedbackAppGlobalState } = useContext(GlobalStateContext);
    const [result, setResult] = feedbackAppGlobalState;
    const { id } = useParams();
    const [recordBoxID, setRecordBoxID] = React.useState(-1);
    const [textBoxID, setTextBoxID] = React.useState(-1);

    const criteriaStateHandle = (criteriaId, value) => {
        const shallowCopy = Object.assign([], result.data.criteria_replies);
        for (let i = 0; i < shallowCopy.length; i++) {
            if (shallowCopy[i].criteria === criteriaId) {
                shallowCopy[i].state = value;
                break;
            }
        }
        setResult({
            ...result,
            data: { ...result.data, criteria_replies: shallowCopy },
        });
    };

    const voiceFileHandle = (file, remove = false, criteriaId) => {
        if (criteriaId) {
            const shallowCopy = Object.assign([], result.data.criteria_replies);
            for (let i = 0; i < shallowCopy.length; i++) {
                if (shallowCopy[i].criteria === criteriaId) {
                    if (remove) {
                        delete shallowCopy[i].voice;
                    } else {
                        shallowCopy[i].voice = file;
                    }
                    break;
                }
            }
            setResult({
                ...result,
                data: { ...result.data, criteria_replies: shallowCopy },
            });
        } else if (recordBoxID === 0) {
            setResult({ ...result, data: { ...result.data, voice: file } });
            setRecordBoxID(-1);
        } else {
            const shallowCopy = Object.assign([], result.data.criteria_replies);
            for (let i = 0; i < shallowCopy.length; i++) {
                if (shallowCopy[i].criteria === recordBoxID) {
                    if (remove) {
                        delete shallowCopy[i].voice;
                    } else {
                        shallowCopy[i].voice = file;
                    }
                    break;
                }
            }
            setResult({
                ...result,
                data: { ...result.data, criteria_replies: shallowCopy },
            });
            setRecordBoxID(-1);
        }
    };

    const fileSelectorHandle = (criteriaId, file, remove = false) => {
        if (file || remove) {
            if (criteriaId === 0) {
                setResult({ ...result, data: { ...result.data, file } });
            } else {
                const shallowCopy = Object.assign(
                    [],
                    result.data.criteria_replies
                );
                for (let i = 0; i < shallowCopy.length; i++) {
                    if (shallowCopy[i].criteria === criteriaId) {
                        if (remove) {
                            delete shallowCopy[i].files;
                        } else {
                            shallowCopy[i].files = file;
                        }
                        break;
                    }
                }
                setResult({
                    ...result,
                    data: { ...result.data, criteria_replies: shallowCopy },
                });
            }
        }
    };

    const noteHandle = (value, targetId = 0) => {
        if (targetId === 0) {
            setResult({
                ...result,
                data: { ...result.data, description: value },
            });
        } else {
            const shallowCopy = Object.assign([], result.data.criteria_replies);
            for (let i = 0; i < shallowCopy.length; i++) {
                if (shallowCopy[i].criteria === targetId) {
                    shallowCopy[i].description = value;
                    break;
                }
            }
            setResult({
                ...result,
                data: { ...result.data, criteria_replies: shallowCopy },
            });
        }
    };

    const scoreHandle = (score) => {
        setResult({ ...result, data: { ...result.data, score } });
    };

    const overalScoreHandle = (key) => {
        const shallowCopy = Object.assign([], result.data);
        delete shallowCopy[key];
        setResult({ ...result, data: { ...shallowCopy } });
    };

    const [stage, setStage] = useState(1);
    const isStepForm =
        result.ajax?.feedback_form_display_mode === FEEDBACK_FORM_DISPLAY_STEP;

    const goToFinalStage = () => setStage(2);

    const sharedFormProps = {
        result,
        noteHandle,
        scoreHandle,
        setRecordBoxID,
        fileSelectorHandle,
        overalScoreHandle,
        textBoxID,
        setTextBoxID,
        criteriaStateHandle,
        voiceFileHandle,
    };

    return (
        <>
            {result.data.form > 0 ? (
                stage === 1 ? (
                    <div className="main-container rating-flow">
                        {result.data.criteria_replies ? (
                            <RecorderBox
                                close={() => setRecordBoxID(-1)}
                                boxId={recordBoxID}
                                file64={(f) => voiceFileHandle(f)}
                            />
                        ) : null}
                        {isStepForm ? (
                            <MultiStepRatingForm
                                {...sharedFormProps}
                                onFinish={goToFinalStage}
                            />
                        ) : (
                            <div className="rating">
                                <OverallScoreSection {...sharedFormProps} />
                                <CriteriaSorter
                                    criteriaList={result.ajax.form.criteria_list.sort(
                                        (a, b) => a.weight - b.weight
                                    )}
                                    criteriaReplies={
                                        result.data.criteria_replies
                                    }
                                    noteHandle={noteHandle}
                                    openTextBoxID={textBoxID}
                                    setTextBoxID={setTextBoxID}
                                    fileSelectorHandle={fileSelectorHandle}
                                    setRecordBoxID={(e) => setRecordBoxID(e)}
                                    criteriaStateHandle={criteriaStateHandle}
                                    voiceFileHandle={voiceFileHandle}
                                />
                                <DBButton
                                    className="send-button"
                                    caption={t("send.label")}
                                    onClick={() => {
                                        goToFinalStage();
                                        setTimeout(() => {
                                            scrollIntoView(
                                                "#final-form-title",
                                                240
                                            );
                                        }, 400);
                                    }}
                                />
                            </div>
                        )}
                        <div className="participation-reward-row">
                            <RewardBadge
                                action="feedback_submit"
                                targetType="item"
                                targetId={result.data.item}
                                opportunity={
                                    result.ajax?.incentive_opportunities
                                        ?.feedback_submit
                                }
                            />
                        </div>
                    </div>
                ) : (
                    <FinalFormModal back={() => setStage(1)} />
                )
            ) : (
                <div className="loader">
                    <Navigate to={`/feedback/${id}`} />
                </div>
            )}
        </>
    );
}
